<template>
    <CommonDialog :value="value" :title="title" :fullscreen="true" show-action-toolbar @input="$emit('input', false)">
        <div class="qr-page">
            <div class="qr-layout">
                <!-- settings -->
                <v-card flat class="qr-card qr-settings">
                    <div class="qr-sec-head">
                        <span class="qr-sec-ic"><v-icon small>mdi-tune-variant</v-icon></span>
                        <span>{{ $t('QrCode') }}</span>
                    </div>
                    <div class="qr-form">
                        <div class="qr-field">
                            <label class="qr-label">{{ $t('QRCodeLabel') }}</label>
                            <v-text-field v-model="label" outlined dense hide-details prepend-inner-icon="mdi-format-text" />
                        </div>
                        <div class="qr-field">
                            <label class="qr-label">{{ $t('UploadLogo') }}</label>
                            <v-file-input v-model="logoFile" outlined dense hide-details accept="image/*" prepend-icon="" prepend-inner-icon="mdi-image-plus-outline" truncate-length="30" @change="handleFileUpload" />
                        </div>
                        <div class="qr-row">
                            <div class="qr-field">
                                <label class="qr-label">{{ $t('Width') }}</label>
                                <v-text-field v-model.number="qrOptions.width" type="number" min="128" max="1024" step="32" outlined dense hide-details suffix="px" />
                            </div>
                            <div class="qr-field">
                                <label class="qr-label">{{ $t('Height') }}</label>
                                <v-text-field v-model.number="qrOptions.height" type="number" min="128" max="1024" step="32" outlined dense hide-details suffix="px" />
                            </div>
                        </div>
                        <div class="qr-row">
                            <div class="qr-field">
                                <label class="qr-label">{{ $t('LinesColor') }}</label>
                                <div class="qr-color">
                                    <input v-model="qrOptions.color.dark" type="color" class="qr-color-input" :aria-label="$t('LinesColor')" />
                                    <span class="qr-color-hex">{{ qrOptions.color.dark }}</span>
                                    <div class="qr-swatches">
                                        <button v-for="c in darkPresets" :key="c" type="button" class="qr-swatch" :class="{ 'is-on': qrOptions.color.dark === c }" :style="{ background: c }" :aria-label="c" @click="qrOptions.color.dark = c"></button>
                                    </div>
                                </div>
                            </div>
                            <div class="qr-field">
                                <label class="qr-label">{{ $t('BackgroundColor') }}</label>
                                <div class="qr-color">
                                    <input v-model="qrOptions.color.light" type="color" class="qr-color-input" :aria-label="$t('BackgroundColor')" />
                                    <span class="qr-color-hex">{{ qrOptions.color.light }}</span>
                                    <div class="qr-swatches">
                                        <button v-for="c in lightPresets" :key="c" type="button" class="qr-swatch" :class="{ 'is-on': qrOptions.color.light === c }" :style="{ background: c }" :aria-label="c" @click="qrOptions.color.light = c"></button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </v-card>

                <!-- preview -->
                <v-card flat class="qr-card qr-preview">
                    <div class="qr-sec-head">
                        <span class="qr-sec-ic"><v-icon small>mdi-qrcode</v-icon></span>
                        <span>{{ $t('Preview') }}</span>
                    </div>
                    <div class="qr-stage">
                        <div ref="qrcode" class="qrcode-container"></div>
                    </div>
                    <div class="qr-link" v-if="surveyURL">
                        <v-icon small class="qr-link-ic">mdi-link-variant</v-icon>
                        <span class="qr-link-text" dir="ltr">{{ surveyURL }}</span>
                    </div>
                    <div class="qr-actions">
                        <v-btn class="qr-btn qr-btn--primary" depressed color="primary" @click="downloadQRCode">
                            <v-icon small class="me-1">mdi-download-outline</v-icon>{{ $t('DOWNLOADQRCODE') }}
                        </v-btn>
                    </div>
                </v-card>
            </div>
        </div>
    </CommonDialog>
</template>

<script>
    import QRCode from 'qrcode'
    import CommonDialog from '@/components/controls/CommonDialog'

    export default {
        components: {
            CommonDialog
        },
        props: {
            title: {
                type: String,
                default: ''
            },
            surveyURL: {
                type: String,
                default: ''
            },
            value: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                label: '',
                logoFile: null,
                logoImage: null,
                darkPresets: ['#000000', '#0a716e', '#1B8354', '#1f2a28', '#b91c1c'],
                lightPresets: ['#ffffff', '#f3f6f5', '#e6f2f1', '#fef3c7', '#fee2e2'],
                qrOptions: {
                    width: 256,
                    height: 256,
                    color: {
                        dark: '#000000',
                        light: '#ffffff'
                    },
                    errorCorrectionLevel: 'H' // Use string 'H' for high error correction level
                }
            }
        },
        watch: {
            // the dialog body mounts lazily: draw once it is actually open
            value(open) {
                if (open) this.$nextTick(() => this.generateQRCode())
            },
            surveyURL: 'generateQRCode',
            label: 'generateQRCode',
            'qrOptions.width': 'generateQRCode',
            'qrOptions.height': 'generateQRCode',
            'qrOptions.color.dark': 'generateQRCode',
            'qrOptions.color.light': 'generateQRCode'
        },
        async mounted() {
            this.$nextTick().then(() => {
                this.generateQRCode()
            })
        },
        methods: {
            async generateQRCode() {
                const qrcodeElement = this.$refs.qrcode

                if (!qrcodeElement) {
                    return
                }

                qrcodeElement.innerHTML = '' // Clear previous QR code

                const canvas = document.createElement('canvas')
                const context = canvas.getContext('2d')
                const qrCodeCanvas = document.createElement('canvas')

                await QRCode.toCanvas(qrCodeCanvas, this.surveyURL || this.label, {
                    width: this.qrOptions?.width || 256,
                    height: this.qrOptions?.height || 256,
                    color: {
                        dark: this.qrOptions?.color?.dark || '#000000',
                        light: this.qrOptions?.color?.light || '#ffffff'
                    },
                    errorCorrectionLevel: this.qrOptions?.errorCorrectionLevel || 'H'
                })

                canvas.width = qrCodeCanvas.width
                canvas.height = qrCodeCanvas.height + 30 // Adjust height to include label

                // keep the label strip on the chosen background so the exported PNG has no transparent band
                context.fillStyle = this.qrOptions?.color?.light || '#ffffff'
                context.fillRect(0, 0, canvas.width, canvas.height)
                context.drawImage(qrCodeCanvas, 0, 0)

                if (this.logoImage) {
                    const logoSize = qrCodeCanvas.width / 5 // Adjust logo size
                    const x = (qrCodeCanvas.width - logoSize) / 2
                    const y = (qrCodeCanvas.height - logoSize) / 2
                    context.drawImage(this.logoImage, x, y, logoSize, logoSize)
                }

                // Add label below QR code
                context.font = '16px Arial'
                context.textAlign = 'center'
                context.fillStyle = this.qrOptions?.color?.dark || '#000000'
                context.fillText(this.label, canvas.width / 2, qrCodeCanvas.height + 20)

                qrcodeElement.appendChild(canvas)
            },
            handleFileUpload(file) {
                if (file) {
                    const reader = new FileReader()
                    reader.onload = (e) => {
                        this.logoImage = new Image()
                        this.logoImage.src = e.target.result
                        this.logoImage.onload = () => this.generateQRCode()
                    }
                    reader.readAsDataURL(file)
                } else {
                    this.logoImage = null
                    this.generateQRCode()
                }
            },
            downloadQRCode() {
                const canvas = this.$refs.qrcode.querySelector('canvas')
                if (canvas) {
                    const dataURL = canvas.toDataURL('image/png')
                    const link = document.createElement('a')
                    link.href = dataURL
                    link.download = 'qrcode.png'
                    link.click()
                }
            }
        }
    }
</script>

<style scoped>
    .qr-page {
        --qr-surface: #f3f6f5;
        --qr-line: #e6ecea;
        --qr-text: #1f2a28;
        --qr-muted: #6b7a77;
        --qr-primary: #0a716e;
        --qr-primary-tint: #e6f2f1;
        --qr-ease: cubic-bezier(.2, .8, .2, 1);
        padding: 22px 20px 32px;
        background: #fff;
        min-height: calc(100vh - 64px);
    }

    .qr-layout {
        display: grid;
        grid-template-columns: minmax(0, 1.4fr) minmax(320px, 1fr);
        gap: 18px;
        max-width: 1100px;
        margin: 0 auto;
        align-items: start;
    }

    .qr-card {
        border: 1px solid var(--qr-line) !important;
        border-radius: 18px !important;
        box-shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px -16px rgba(15, 23, 42, .18) !important;
        overflow: hidden;
    }

    .qr-sec-head {
        display: flex; align-items: center; gap: 10px;
        padding: 14px 18px; font-size: 15px; font-weight: 800; color: var(--qr-text);
        border-bottom: 1px solid var(--qr-line);
    }
    .qr-sec-ic {
        display: inline-flex; align-items: center; justify-content: center;
        width: 32px; height: 32px; border-radius: 10px; background: var(--qr-primary-tint);
    }
    .qr-sec-ic .v-icon { color: var(--qr-primary) !important; }

    /* ---------- settings ---------- */
    .qr-form { display: flex; flex-direction: column; gap: 16px; padding: 18px; }
    .qr-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
    .qr-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
    .qr-label { font-size: 12px; font-weight: 700; color: var(--qr-muted); }
    .qr-field ::v-deep .v-input__slot {
        min-height: 40px !important; border-radius: 12px !important; background: var(--qr-surface);
        box-shadow: none !important;
    }
    .qr-field ::v-deep fieldset { border-color: var(--qr-line) !important; }
    .qr-field ::v-deep .v-input--is-focused fieldset { border-color: var(--qr-primary) !important; }
    .qr-field ::v-deep .v-input--is-focused .v-input__slot { background: #fff; }
    .qr-field ::v-deep input { font-size: 13.5px; color: var(--qr-text); }
    .qr-field ::v-deep .v-input__prepend-inner .v-icon,
    .qr-field ::v-deep .v-input__append-inner .v-icon { color: var(--qr-muted) !important; }
    .qr-field ::v-deep .v-input__prepend-outer { display: none; }
    .qr-field ::v-deep .v-text-field__suffix { font-size: 12px; color: var(--qr-muted); }

    .qr-color {
        display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
        padding: 8px 10px; border: 1px solid var(--qr-line); border-radius: 12px; background: var(--qr-surface);
    }
    .qr-color-input {
        width: 40px; height: 32px; padding: 0; border: 1px solid var(--qr-line); border-radius: 9px;
        background: transparent; cursor: pointer;
    }
    .qr-color-input::-webkit-color-swatch-wrapper { padding: 3px; }
    .qr-color-input::-webkit-color-swatch { border: 0; border-radius: 6px; }
    .qr-color-hex { font-size: 12px; font-weight: 700; color: var(--qr-text); font-family: ui-monospace, Consolas, monospace; direction: ltr; }
    .qr-swatches { display: flex; gap: 6px; margin-inline-start: auto; }
    .qr-swatch {
        width: 22px; height: 22px; border-radius: 50%; border: 2px solid #fff; cursor: pointer; padding: 0;
        box-shadow: 0 0 0 1px var(--qr-line);
        transition: transform .15s var(--qr-ease), box-shadow .15s var(--qr-ease);
    }
    .qr-swatch:hover { transform: scale(1.1); }
    .qr-swatch.is-on { box-shadow: 0 0 0 2px var(--qr-primary); }
    .qr-swatch:focus { outline: none; }
    .qr-swatch:focus-visible { box-shadow: 0 0 0 2px #fff, 0 0 0 4px var(--qr-primary); }

    /* ---------- preview ---------- */
    .qr-stage {
        display: flex; align-items: center; justify-content: center;
        min-height: 320px; padding: 24px;
        background:
            linear-gradient(45deg, #f3f6f5 25%, transparent 25%, transparent 75%, #f3f6f5 75%),
            linear-gradient(45deg, #f3f6f5 25%, transparent 25%, transparent 75%, #f3f6f5 75%);
        background-size: 20px 20px;
        background-position: 0 0, 10px 10px;
        background-color: #fff;
    }
    .qrcode-container { display: inline-flex; padding: 14px; border-radius: 16px; background: #fff; box-shadow: 0 1px 2px rgba(15, 23, 42, .05), 0 12px 28px -14px rgba(15, 23, 42, .3); }
    .qrcode-container ::v-deep canvas { display: block; max-width: 100%; height: auto; border-radius: 8px; }

    .qr-link {
        display: flex; align-items: center; gap: 8px;
        margin: 0 18px; padding: 10px 12px; border-radius: 12px; background: var(--qr-surface);
    }
    .qr-link-ic { color: var(--qr-muted) !important; }
    .qr-link-text { flex: 1 1 auto; min-width: 0; font-size: 12.5px; color: var(--qr-text); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: left; }

    .qr-actions { display: flex; justify-content: center; padding: 16px 18px 18px; }
    .qr-btn { border-radius: 12px !important; height: 42px !important; padding: 0 20px !important; font-weight: 700; letter-spacing: 0; text-transform: none; }
    .qr-btn--primary { box-shadow: 0 8px 18px -10px var(--qr-primary) !important; }

    @media (max-width: 960px) {
        .qr-layout { grid-template-columns: 1fr; }
    }
    @media (max-width: 600px) {
        .qr-page { padding: 14px 12px 24px; }
        .qr-row { grid-template-columns: 1fr; }
        .qr-swatches { margin-inline-start: 0; }
    }
</style>
